const DAY_MS = 24 * 60 * 60 * 1000;

const normalizeId = (value) => String(value || '').trim();

const toDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const toFiniteOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const RISK_ORDER = { critical: 3, high: 2, warning: 1, safe: 0 };

const normalizeStrategy = (value) => {
  const raw = String(value || 'balanced').trim().toLowerCase();
  if (raw === 'cost' || raw === 'hemat' || raw === 'hemat-biaya') return 'cost';
  if (raw === 'growth' || raw === 'siap' || raw === 'siap-pertumbuhan') return 'growth';
  return 'balanced';
};

const inferRisk = (occupancyPercent, daysToFull, monthlyGrowth) => {
  const occ = Number(occupancyPercent || 0);
  const eta = Number.isFinite(daysToFull) ? Number(daysToFull) : null;
  const growth = Number(monthlyGrowth || 0);

  if (occ >= 95 || (eta !== null && eta <= 14)) {
    return { level: 'critical', label: 'Kritis', tone: 'critical' };
  }
  if (occ >= 85 || (eta !== null && eta <= 30) || growth >= 6) {
    return { level: 'high', label: 'Tinggi', tone: 'high' };
  }
  if (occ >= 70 || (eta !== null && eta <= 60) || growth >= 3) {
    return { level: 'warning', label: 'Waspada', tone: 'warning' };
  }
  return { level: 'safe', label: 'Aman', tone: 'safe' };
};

const collectRecentGrowth = (customers, referenceDate = new Date()) => {
  const dateNow = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  const windows = {
    d30: 0,
    d60: 0,
    d90: 0,
  };

  customers.forEach((customer) => {
    const createdAt = toDate(customer?.created_at || customer?.createdAt || customer?.registered_at);
    if (!createdAt) return;
    const ageDays = Math.max(0, Math.floor((dateNow.getTime() - createdAt.getTime()) / DAY_MS));
    if (ageDays <= 30) windows.d30 += 1;
    if (ageDays <= 60) windows.d60 += 1;
    if (ageDays <= 90) windows.d90 += 1;
  });

  return windows;
};

export const getRiskBadgeClass = (tone = 'safe') => {
  if (tone === 'critical') return 'border-rose-400/45 bg-rose-500/15 text-rose-200';
  if (tone === 'high') return 'border-orange-400/45 bg-orange-500/15 text-orange-200';
  if (tone === 'warning') return 'border-amber-400/45 bg-amber-500/15 text-amber-200';
  return 'border-emerald-400/45 bg-emerald-500/15 text-emerald-200';
};

export const getUrgencyBadgeClass = (tone = 'low') => {
  if (tone === 'critical') return 'border-rose-400/45 bg-rose-500/15 text-rose-200';
  if (tone === 'high') return 'border-orange-400/45 bg-orange-500/15 text-orange-200';
  if (tone === 'medium') return 'border-amber-400/45 bg-amber-500/15 text-amber-200';
  return 'border-sky-400/45 bg-sky-500/15 text-sky-200';
};

export const getRecommendationTypeBadgeClass = (type = 'monitor') => {
  if (type === 'capacity_expansion') return 'border-cyan-400/45 bg-cyan-500/15 text-cyan-200';
  if (type === 'planning') return 'border-indigo-400/45 bg-indigo-500/15 text-indigo-200';
  if (type === 'rebalance') return 'border-fuchsia-400/45 bg-fuchsia-500/15 text-fuchsia-200';
  return 'border-slate-400/45 bg-slate-500/15 text-slate-200';
};

export const getRecommendationTypeLabel = (type = 'monitor') => {
  if (type === 'capacity_expansion') return 'Capacity Expansion';
  if (type === 'planning') return 'Planning';
  if (type === 'rebalance') return 'Rebalance';
  return 'Monitor';
};

const toUrgencyMeta = (riskLevel = 'safe') => {
  if (riskLevel === 'critical') return { level: 'kritis', label: 'Kritis', tone: 'critical', rank: 4 };
  if (riskLevel === 'high') return { level: 'tinggi', label: 'Tinggi', tone: 'high', rank: 3 };
  if (riskLevel === 'warning') return { level: 'sedang', label: 'Sedang', tone: 'medium', rank: 2 };
  return { level: 'rendah', label: 'Rendah', tone: 'low', rank: 1 };
};

const toConfidenceLabel = (score) => {
  const value = Number(score || 0);
  if (value >= 0.72) return 'Tinggi';
  if (value >= 0.48) return 'Sedang';
  return 'Rendah';
};

const computeOdpConfidence = (prediction) => {
  const occupancy = Number(prediction?.occupancyPercent || 0);
  const growth = Number(prediction?.growth30d || 0);
  const daysToFull = toFiniteOrNull(prediction?.daysToFull);
  const clusterPressure = Number(prediction?.used || 0) >= 10 ? 1 : clamp((Number(prediction?.used || 0) - 4) / 8, 0, 1);

  const occupancyPressure = clamp((occupancy - 55) / 45, 0, 1);
  const growthPressure = clamp(growth / 6, 0, 1);
  const etaPressure = daysToFull === null ? 0.32 : clamp((90 - daysToFull) / 90, 0, 1);

  const rawScore = clamp(
    (occupancyPressure * 0.36)
    + (growthPressure * 0.28)
    + (etaPressure * 0.24)
    + (clusterPressure * 0.12),
    0,
    1,
  );
  const score = Number(rawScore.toFixed(2));

  const weakSignals = occupancy < 68 && growth <= 1 && (daysToFull === null || daysToFull > 90);

  return {
    score,
    label: toConfidenceLabel(score),
    signals: { occupancyPressure, growthPressure, etaPressure, clusterPressure },
    weakSignals,
  };
};

const computeNasConfidence = (prediction) => {
  const occupancy = Number(prediction?.syntheticOccupancy || 0);
  const growth = Number(prediction?.growth30d || 0);
  const daysToPressure = toFiniteOrNull(prediction?.syntheticDaysToPressure);
  const demandPressure = Number(prediction?.activeCount || 0) >= 120 ? 1 : clamp((Number(prediction?.activeCount || 0) - 40) / 80, 0, 1);

  const occupancyPressure = clamp((occupancy - 50) / 50, 0, 1);
  const growthPressure = clamp(growth / 6, 0, 1);
  const etaPressure = daysToPressure === null ? 0.3 : clamp((120 - daysToPressure) / 120, 0, 1);

  const rawScore = clamp(
    (occupancyPressure * 0.34)
    + (growthPressure * 0.34)
    + (etaPressure * 0.2)
    + (demandPressure * 0.12),
    0,
    1,
  );
  const score = Number(rawScore.toFixed(2));

  const weakSignals = occupancy < 60 && growth <= 1 && (daysToPressure === null || daysToPressure > 120);

  return {
    score,
    label: toConfidenceLabel(score),
    signals: { occupancyPressure, growthPressure, etaPressure, demandPressure },
    weakSignals,
  };
};

const recommendOdpCapacity = (prediction, strategy = 'balanced') => {
  const safeStrategy = normalizeStrategy(strategy);
  const used = Number(prediction?.used || 0);
  const growth30d = Number(prediction?.growth30d || 0);
  const projectedMonths = safeStrategy === 'cost' ? 1.2 : safeStrategy === 'growth' ? 3 : 2;
  const projectedDemand = Math.max(used + growth30d * projectedMonths, used + 1); // proyeksi ringan berbasis strategi
  const capacities = [8, 16, 24];
  const targetUtilization = safeStrategy === 'cost' ? 0.9 : safeStrategy === 'growth' ? 0.64 : 0.78;

  const picked = capacities.find((cap) => projectedDemand <= cap * targetUtilization) || 24;
  return { value: picked, projectedDemand, strategy: safeStrategy };
};

const buildOdpRecommendation = (prediction, strategy = 'balanced') => {
  const safeStrategy = normalizeStrategy(strategy);
  const urgency = toUrgencyMeta(prediction?.risk?.level);
  const suggested = recommendOdpCapacity(prediction, safeStrategy);
  const riskLevel = String(prediction?.risk?.level || 'safe');
  const remaining = Number(prediction?.remaining || 0);
  const occupancyPercent = Number(prediction?.occupancyPercent || 0);
  const growth30d = Number(prediction?.growth30d || 0);
  const daysToFull = toFiniteOrNull(prediction?.daysToFull);

  const isExpansion = riskLevel === 'critical'
    || riskLevel === 'high'
    || (daysToFull !== null && daysToFull <= 30)
    || (remaining <= 1 && occupancyPercent >= 85)
    || (remaining <= 2 && occupancyPercent >= 78 && growth30d >= 2);

  const isPlanning = !isExpansion
    && (riskLevel === 'warning' || (remaining <= 2 && growth30d >= 1));
  const actionType = isExpansion ? 'capacity_expansion' : isPlanning ? 'planning' : 'monitor';
  const confidence = computeOdpConfidence(prediction);

  const reasonLines = [
    `Okupansi ${Number(prediction?.occupancyPercent || 0)}% (${Number(prediction?.used || 0)}/${Number(prediction?.capacity || 0)}).`,
    `Sisa kapasitas ${Number(prediction?.remaining || 0)} port.`,
    `Growth 30 hari +${Number(prediction?.growth30d || 0)} pelanggan.`,
  ];
  if (Number.isFinite(daysToFull)) {
    reasonLines.push(`Estimasi penuh dalam ${Number(daysToFull)} hari.`);
  } else {
    reasonLines.push('Estimasi penuh belum cukup data historis.');
  }

  const suggestedAction = isExpansion
    ? `Pertimbangkan tambah ODP ${suggested.value} di sekitar ${prediction?.name || 'cluster ini'}.`
    : isPlanning
      ? `Prioritaskan analisis planning di area ${prediction?.name || '-'}.`
      : `Pantau ${prediction?.name || 'ODP'} berkala, belum perlu ekspansi saat ini.`;

  return {
    id: `odp:${String(prediction?.id || '')}`,
    sourceType: 'odp',
    sourceId: String(prediction?.id || ''),
    sourceLabel: String(prediction?.name || 'ODP'),
    risk: prediction?.risk || { level: 'safe', label: 'Aman', tone: 'safe' },
    urgency,
    actionType,
    title: isExpansion ? `Tekanan kapasitas ${prediction?.name || 'ODP'}` : `Monitoring kapasitas ${prediction?.name || 'ODP'}`,
    summary: suggestedAction,
    suggestedAction,
    suggestedOdpCapacity: isExpansion || isPlanning ? suggested.value : null,
    strategy: safeStrategy,
    confidenceScore: Number(confidence.score.toFixed(2)),
    confidenceLabel: confidence.label,
    weakSignals: confidence.weakSignals,
    confidenceSignals: confidence.signals,
    reasonLines,
    score: (RISK_ORDER[prediction?.risk?.level] || 0) * 100 + Number(prediction?.occupancyPercent || 0),
    metrics: {
      occupancyPercent: Number(prediction?.occupancyPercent || 0),
      remaining: Number(prediction?.remaining || 0),
      growth30d: Number(prediction?.growth30d || 0),
      daysToFull,
    },
    links: {
      monitoring: `/topology?mode=live&sourceType=odp&sourceId=${encodeURIComponent(String(prediction?.id || ''))}`,
      planning: `/topology?mode=plan&context=planning&planningAction=ai-odp&sourceType=odp&sourceId=${encodeURIComponent(String(prediction?.id || ''))}`,
    },
  };
};

const buildNasRecommendation = (prediction, fallbackTargets = [], strategy = 'balanced') => {
  const safeStrategy = normalizeStrategy(strategy);
  const urgency = toUrgencyMeta(prediction?.risk?.level);
  const confidence = computeNasConfidence(prediction);
  const riskLevel = String(prediction?.risk?.level || 'safe');
  const targetAlternative = fallbackTargets.find((item) => String(item?.id || '') !== String(prediction?.id || ''));

  let actionType = 'monitor';
  let suggestedAction = `Lanjutkan assignment pelanggan baru ke ${prediction?.name || 'NAS'} dengan monitoring berkala.`;
  if (riskLevel === 'critical' || riskLevel === 'high') {
    actionType = 'rebalance';
    suggestedAction = targetAlternative
      ? `Alihkan assignment pelanggan baru dari ${prediction?.name || 'NAS'} ke ${targetAlternative.name}.`
      : `Kurangi assignment baru di ${prediction?.name || 'NAS'} dan evaluasi balancing site.`;
  } else if (riskLevel === 'warning') {
    actionType = 'planning';
    suggestedAction = `Review distribusi assignment di ${prediction?.name || 'NAS'} sebelum growth meningkat.`;
  }

  const reasonLines = [
    `Pelanggan aktif ${Number(prediction?.activeCount || 0)}.`,
    `Growth 30 hari +${Number(prediction?.growth30d || 0)} pelanggan.`,
    `Tekanan sintetis ${Number(prediction?.syntheticOccupancy || 0)}%.`,
  ];
  const syntheticDaysToPressure = toFiniteOrNull(prediction?.syntheticDaysToPressure);
  if (Number.isFinite(syntheticDaysToPressure)) {
    reasonLines.push(`Estimasi tekanan dalam ${Number(syntheticDaysToPressure)} hari.`);
  } else {
    reasonLines.push('Estimasi tekanan belum cukup data historis.');
  }
  if (targetAlternative) {
    reasonLines.push(`Alternatif seimbang: ${targetAlternative.name}.`);
  }

  return {
    id: `nas:${String(prediction?.id || '')}`,
    sourceType: 'nas',
    sourceId: String(prediction?.id || ''),
    sourceLabel: String(prediction?.name || 'NAS'),
    risk: prediction?.risk || { level: 'safe', label: 'Aman', tone: 'safe' },
    urgency,
    actionType,
    title: actionType === 'rebalance' ? `Rebalance ${prediction?.name || 'NAS'}` : `Review kapasitas ${prediction?.name || 'NAS'}`,
    summary: suggestedAction,
    suggestedAction,
    strategy: safeStrategy,
    confidenceScore: Number(confidence.score.toFixed(2)),
    confidenceLabel: confidence.label,
    weakSignals: confidence.weakSignals,
    confidenceSignals: confidence.signals,
    reasonLines,
    score: (RISK_ORDER[prediction?.risk?.level] || 0) * 100 + Number(prediction?.growth30d || 0) * 3 + Number(prediction?.activeCount || 0) * 0.1,
    metrics: {
      activeCount: Number(prediction?.activeCount || 0),
      growth30d: Number(prediction?.growth30d || 0),
      syntheticDaysToPressure,
    },
    alternativeNas: targetAlternative ? { id: String(targetAlternative.id || ''), name: String(targetAlternative.name || '') } : null,
    links: {
      monitoring: `/topology?mode=live&sourceType=nas&sourceId=${encodeURIComponent(String(prediction?.id || ''))}`,
      nas: `/dadu/voucher?tab=nas&focusNas=${encodeURIComponent(String(prediction?.id || ''))}`,
    },
  };
};

export const buildCapacityRecommendations = ({ odpPredictions = [], nasPredictions = [], strategy = 'balanced', limit = 8 } = {}) => {
  const safeStrategy = normalizeStrategy(strategy);
  const safeNasTargets = [...(Array.isArray(nasPredictions) ? nasPredictions : [])]
    .filter((item) => String(item?.risk?.level || 'safe') === 'safe' || String(item?.risk?.level || 'safe') === 'warning')
    .sort((a, b) => {
      const riskDiff = (RISK_ORDER[a?.risk?.level] || 0) - (RISK_ORDER[b?.risk?.level] || 0);
      if (riskDiff !== 0) return riskDiff;
      return Number(a?.activeCount || 0) - Number(b?.activeCount || 0);
    });

  const odpRecommendations = (Array.isArray(odpPredictions) ? odpPredictions : [])
    .map((item) => buildOdpRecommendation(item, safeStrategy));
  const nasRecommendations = (Array.isArray(nasPredictions) ? nasPredictions : [])
    .map((item) => buildNasRecommendation(item, safeNasTargets, safeStrategy));

  const suppressed = [];
  const guarded = [...odpRecommendations, ...nasRecommendations]
    .map((item) => {
      const confidenceScore = Number(item?.confidenceScore || 0);
      if (Number(item?.weakSignals ? 1 : 0) > 0 && confidenceScore < 0.5 && item?.actionType !== 'monitor') {
        return {
          ...item,
          actionType: 'monitor',
          summary: `Pantau ${item?.sourceLabel || item?.sourceType || 'infrastruktur'} sampai sinyal rekomendasi lebih kuat.`,
          suggestedAction: `Pantau ${item?.sourceLabel || item?.sourceType || 'infrastruktur'} sampai sinyal rekomendasi lebih kuat.`,
          guardReason: 'weak_signals_downgraded_to_monitor',
        };
      }
      if (confidenceScore < 0.24) {
        suppressed.push({
          id: item?.id,
          sourceType: item?.sourceType,
          sourceId: item?.sourceId,
          reason: 'low_confidence',
          confidenceScore,
        });
        return null;
      }
      return item;
    })
    .filter(Boolean);

  const all = guarded.sort((a, b) => Number(b?.score || 0) - Number(a?.score || 0));

  const topRecommendations = all.slice(0, Math.max(1, Number(limit || 8)));
  const bySourceKey = {};
  topRecommendations.forEach((item) => {
    const key = `${String(item.sourceType || 'unknown')}:${String(item.sourceId || '')}`;
    bySourceKey[key] = item;
  });

  const summary = {
    critical: topRecommendations.filter((item) => item?.urgency?.level === 'kritis').length,
    high: topRecommendations.filter((item) => item?.urgency?.level === 'tinggi').length,
    planning: topRecommendations.filter((item) => item?.actionType === 'planning').length,
    expansion: topRecommendations.filter((item) => item?.actionType === 'capacity_expansion').length,
    rebalance: topRecommendations.filter((item) => item?.actionType === 'rebalance').length,
  };

  return {
    strategy: safeStrategy,
    recommendations: topRecommendations,
    bySourceKey,
    summary,
    suppressed,
  };
};

export const buildOdpCapacityPredictions = ({ odps = [], customers = [], resolveCustomerOdpId = null }) => {
  const customerBuckets = {};
  customers.forEach((customer) => {
    const odpIdRaw = typeof resolveCustomerOdpId === 'function'
      ? resolveCustomerOdpId(customer)
      : normalizeId(customer?.linked_odp_id || customer?.odp_id);
    const odpId = normalizeId(odpIdRaw);
    if (!odpId) return;
    if (!customerBuckets[odpId]) customerBuckets[odpId] = [];
    customerBuckets[odpId].push(customer);
  });

  return odps.map((odp) => {
    const odpId = normalizeId(odp?.id);
    const linkedCustomers = customerBuckets[odpId] || [];
    const capacity = Math.max(1, Number(odp?.suggested_capacity || odp?.odp_capacity || odp?.capacity || 8));
    const used = linkedCustomers.length;
    const remaining = Math.max(0, capacity - used);
    const occupancyPercent = Math.round(clamp((used / capacity) * 100, 0, 999));

    const growthWindow = collectRecentGrowth(linkedCustomers);
    const monthlyGrowth = Number(growthWindow.d30 || 0);
    const daysToFull = monthlyGrowth > 0 && remaining > 0
      ? Math.max(1, Math.round((remaining / monthlyGrowth) * 30))
      : null;

    const risk = inferRisk(occupancyPercent, daysToFull, monthlyGrowth);

    const insight = risk.level === 'critical'
      ? `${odp?.name || odp?.code || `ODP ${odpId}`} diprediksi penuh dalam ${daysToFull || '<14'} hari.`
      : risk.level === 'high'
        ? `${odp?.name || odp?.code || `ODP ${odpId}`} mendekati limit kapasitas, pertimbangkan ekspansi ODP.`
        : risk.level === 'warning'
          ? `${odp?.name || odp?.code || `ODP ${odpId}`} mulai naik, pantau pertumbuhan pelanggan area ini.`
          : `${odp?.name || odp?.code || `ODP ${odpId}`} masih aman untuk pertumbuhan saat ini.`;

    return {
      id: odpId,
      name: String(odp?.name || odp?.code || `ODP ${odpId}`),
      code: String(odp?.code || ''),
      oltId: normalizeId(odp?.olt_id),
      capacity,
      used,
      remaining,
      occupancyPercent,
      growth30d: growthWindow.d30,
      growth60d: growthWindow.d60,
      growth90d: growthWindow.d90,
      daysToFull,
      risk,
      insight,
    };
  }).sort((a, b) => {
    const order = { critical: 3, high: 2, warning: 1, safe: 0 };
    const riskDiff = order[b.risk.level] - order[a.risk.level];
    if (riskDiff !== 0) return riskDiff;
    return b.occupancyPercent - a.occupancyPercent;
  });
};

export const buildNasCapacityPredictions = ({ nasRouters = [], customers = [], resolveCustomerRouterId = null }) => {
  const customerBuckets = {};
  customers.forEach((customer) => {
    const routerIdRaw = typeof resolveCustomerRouterId === 'function'
      ? resolveCustomerRouterId(customer)
      : normalizeId(customer?.linked_router_id || customer?.router_id);
    const routerId = normalizeId(routerIdRaw);
    if (!routerId) return;
    if (!customerBuckets[routerId]) customerBuckets[routerId] = [];
    customerBuckets[routerId].push(customer);
  });

  return nasRouters.map((nas) => {
    const routerId = normalizeId(nas?.router_id || nas?.id);
    const linkedCustomers = customerBuckets[routerId] || [];
    const activeCount = linkedCustomers.length;
    const growthWindow = collectRecentGrowth(linkedCustomers);
    const monthlyGrowth = Number(growthWindow.d30 || 0);

    const syntheticOccupancy = clamp(activeCount / 180, 0, 1) * 100;
    const syntheticDaysToPressure = monthlyGrowth > 0
      ? Math.max(7, Math.round(((180 - activeCount) / monthlyGrowth) * 30))
      : null;

    const risk = inferRisk(syntheticOccupancy, syntheticDaysToPressure, monthlyGrowth);

    const insight = risk.level === 'critical'
      ? `${nas?.name || nas?.nas_identifier || routerId} mengalami tekanan tinggi, evaluasi distribusi pelanggan.`
      : risk.level === 'high'
        ? `${nas?.name || nas?.nas_identifier || routerId} bertumbuh cepat, siapkan redistribusi NAS.`
        : risk.level === 'warning'
          ? `${nas?.name || nas?.nas_identifier || routerId} mulai meningkat, monitor beban sesi.`
          : `${nas?.name || nas?.nas_identifier || routerId} berada pada zona aman.`;

    return {
      id: routerId,
      name: String(nas?.name || nas?.nas_identifier || `NAS ${routerId}`),
      nasIdentifier: String(nas?.nas_identifier || ''),
      siteName: String(nas?.site_name || nas?.site || ''),
      activeCount,
      growth30d: growthWindow.d30,
      growth60d: growthWindow.d60,
      growth90d: growthWindow.d90,
      syntheticOccupancy: Math.round(syntheticOccupancy),
      syntheticDaysToPressure,
      risk,
      insight,
    };
  }).sort((a, b) => {
    const order = { critical: 3, high: 2, warning: 1, safe: 0 };
    const riskDiff = order[b.risk.level] - order[a.risk.level];
    if (riskDiff !== 0) return riskDiff;
    return b.activeCount - a.activeCount;
  });
};

export const buildCapacityInsights = ({ odpPredictions = [], nasPredictions = [] }) => {
  const insights = [];
  const highestRiskOdp = odpPredictions.find((item) => item.risk.level === 'critical' || item.risk.level === 'high') || odpPredictions[0];
  const fastestNas = [...nasPredictions].sort((a, b) => (b.growth30d || 0) - (a.growth30d || 0))[0];

  if (highestRiskOdp) {
    if (Number.isFinite(highestRiskOdp.daysToFull)) {
      insights.push(`${highestRiskOdp.name} diprediksi penuh dalam ${highestRiskOdp.daysToFull} hari.`);
    } else {
      insights.push(`${highestRiskOdp.name} saat ini terisi ${highestRiskOdp.occupancyPercent}% (${highestRiskOdp.used}/${highestRiskOdp.capacity}).`);
    }
  }

  if (fastestNas) {
    insights.push(`${fastestNas.name} memiliki pertumbuhan pelanggan tertinggi 30 hari terakhir (+${fastestNas.growth30d}).`);
  }

  const criticalOdps = odpPredictions.filter((item) => item.risk.level === 'critical').length;
  if (criticalOdps > 0) {
    insights.push(`Ada ${criticalOdps} ODP berstatus Kritis. Pertimbangkan penambahan ODP 8/16/24 sesuai kepadatan area.`);
  }

  if (insights.length === 0) {
    insights.push('Kapasitas infrastruktur masih stabil pada scope saat ini.');
  }

  return insights.slice(0, 4);
};
